import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Users, Send, Star, MapPin, ChevronDown, ChevronUp, Info, Scale, Layers, UserCog, CheckSquare, Square } from 'lucide-react';
import api from '../services/api';
import { verstuurCentraleUitnodiging, type Tariefmodus } from '../services/poolOnderhandelingApi';

/**
 * Centrale pool-uitnodiging (Stap 4, migratie 130).
 *
 * De opdrachtgever nodigt in één verzending professionals uit meerdere
 * disciplines tegelijk uit (psychologen, coaches, BMW'ers, …). De
 * uitnodigingstekst is voor iedereen gelijk; het tarief komt op één van drie
 * manieren tot stand:
 *   MARKTCONFORM  tariefveld open — de professional bepaalt het starttarief
 *   GROEP         één tariefvoorstel per discipline/professionalgroep
 *   INDIVIDUEEL   tariefvoorstel per persoon op basis van cv/ervaring
 * In alle gevallen is de workflow tweezijdig: de professional accepteert of
 * doet een tegenvoorstel, de opdrachtgever hakt de knoop door.
 */

interface Kandidaat {
  professional_id: string;
  naam: string;
  city: string | null;
  photo_url: string | null;
  average_rating: number | null;
  total_reviews: number;
  availability_status: string | null;
  afstand_km: number | null;
  zzp_jaren: number;
  brevet: number;
  brevet_label: string;
  bandbreedte_min: number | null;
  bandbreedte_max: number | null;
  al_in_pool: boolean;
  onderhandeling: {
    id: string;
    status: string;
    bod_werkgever: number | null;
    tegenbod_professional: number | null;
    akkoord_tarief: number | null;
  } | null;
}

interface DisciplineGradatie {
  professional_type: string;
  label: string;
  category: string;
  gewenste_experts: number;
  actieve_experts: number;
  kandidaten: Kandidaat[];
}

interface GradatiePayload {
  disciplines: DisciplineGradatie[];
  vestigingen: Array<{ id: string; naam: string; plaats: string | null }>;
  pool_ingericht: boolean;
  werkgebied_radius_km: number | null;
}

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  REQUEST_SENT: { label: 'Uitnodiging verstuurd', cls: 'bg-blue-100 text-blue-800' },
  COUNTER_PROPOSAL: { label: 'Tegenvoorstel ontvangen', cls: 'bg-amber-100 text-amber-800' },
  AGREED: { label: 'Akkoord — Model-OvO wordt voorbereid', cls: 'bg-green-100 text-green-800' },
  CONTRACT_PENDING: { label: 'Wacht op uw handtekening', cls: 'bg-purple-100 text-purple-800' },
  ACTIVE: { label: 'Live in de pool', cls: 'bg-green-100 text-green-700' },
};

const MODI: Array<{ key: Tariefmodus; titel: string; icon: React.ReactNode; uitleg: string }> = [
  {
    key: 'MARKTCONFORM',
    titel: 'Marktconform uitvragen',
    icon: <Scale className="w-5 h-5" />,
    uitleg: 'Het tariefveld blijft open. De professional geeft bij acceptatie zelf het gewenste uurtarief op; u gaat akkoord of doet een tegenvoorstel.',
  },
  {
    key: 'GROEP',
    titel: 'Tarief per discipline',
    icon: <Layers className="w-5 h-5" />,
    uitleg: 'Eén tariefvoorstel per professionalgroep (bijv. psychologen € 115, coaches € 85). Iedere professional ziet het voorstel voor zijn beroepsgroep.',
  },
  {
    key: 'INDIVIDUEEL',
    titel: 'Tarief op maat per persoon',
    icon: <UserCog className="w-5 h-5" />,
    uitleg: 'U vult per professional een specifiek bedrag in op basis van cv en ervaring. De uitnodigingstekst blijft voor iedereen gelijk.',
  },
];

const euro = (v: number | null | undefined) => (v == null ? '—' : `€${Number(v).toFixed(2)}`);
const key = (type: string, id: string) => `${type}|${id}`;

export const EmployerPoolUitnodigenPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [modus, setModus] = useState<Tariefmodus>('GROEP');
  // ?discipline=arbeidsdeskundige (Poortwachter week 52): alleen die discipline staat opengeklapt
  const focusDiscipline = useSearchParams()[0].get('discipline');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [selectie, setSelectie] = useState<Record<string, boolean>>({}); // key: type|professionalId
  const [biedingen, setBiedingen] = useState<Record<string, string>>({}); // key: type|professionalId -> bedrag (INDIVIDUEEL)
  const [groepstarieven, setGroepstarieven] = useState<Record<string, string>>({}); // key: type -> bedrag (GROEP)
  const [bericht, setBericht] = useState('');
  const [locationId, setLocationId] = useState<string>('');

  const { data: payload, isLoading, error } = useQuery<GradatiePayload>({
    queryKey: ['pool-gradatie'],
    queryFn: async () => (await api.get('/pool/employer/gradatie')).data?.data,
  });

  const verstuurMutation = useMutation({
    mutationFn: async (args: Parameters<typeof verstuurCentraleUitnodiging>[0]) => (await verstuurCentraleUitnodiging(args)).data,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['pool-gradatie'] });
      queryClient.invalidateQueries({ queryKey: ['pool-onderhandelingen-overzicht'] });
      const fouten = data?.data?.fouten ?? [];
      if (fouten.length) {
        toast.error(`${data.data.verstuurd.length} verstuurd, ${fouten.length} mislukt: ${fouten[0]?.reden || ''}`, { duration: 8000 });
      } else {
        toast.success(data?.message || 'Pool-uitnodiging verstuurd');
      }
      // Alleen de succesvol verstuurde selecties leegmaken
      const mislukt = new Set(fouten.map((f) => key(f.professional_type, f.professional_id)));
      setSelectie((s) => Object.fromEntries(Object.entries(s).filter(([k, v]) => v && mislukt.has(k))));
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Versturen mislukt'),
  });

  const geselecteerd = useMemo(() => {
    const lijst: Array<{ d: DisciplineGradatie; k: Kandidaat }> = [];
    for (const d of payload?.disciplines ?? []) {
      for (const k of d.kandidaten) {
        if (selectie[key(d.professional_type, k.professional_id)] && !k.al_in_pool && !k.onderhandeling) lijst.push({ d, k });
      }
    }
    return lijst;
  }, [payload, selectie]);

  const disciplinesInSelectie = useMemo(() => {
    const m = new Map<string, DisciplineGradatie>();
    for (const { d } of geselecteerd) m.set(d.professional_type, d);
    return Array.from(m.values());
  }, [geselecteerd]);

  const toggleSelect = (type: string, k: Kandidaat) => {
    const kk = key(type, k.professional_id);
    setSelectie((s) => ({ ...s, [kk]: !s[kk] }));
    if (!biedingen[kk] && k.bandbreedte_min != null) {
      setBiedingen((b) => ({ ...b, [kk]: String(k.bandbreedte_min) }));
    }
  };

  const toggleDiscipline = (d: DisciplineGradatie) => {
    const beschikbaar = d.kandidaten.filter((k) => !k.al_in_pool && !k.onderhandeling);
    const allesAan = beschikbaar.length > 0 && beschikbaar.every((k) => selectie[key(d.professional_type, k.professional_id)]);
    setSelectie((s) => {
      const n = { ...s };
      for (const k of beschikbaar) n[key(d.professional_type, k.professional_id)] = !allesAan;
      return n;
    });
    if (!allesAan) {
      setBiedingen((b) => {
        const n = { ...b };
        for (const k of beschikbaar) {
          const kk = key(d.professional_type, k.professional_id);
          if (!n[kk] && k.bandbreedte_min != null) n[kk] = String(k.bandbreedte_min);
        }
        return n;
      });
    }
  };

  /** Laagste marktconforme ondergrens onder de geselecteerde kandidaten van een discipline (hint voor het groepstarief). */
  const groepOndergrens = (d: DisciplineGradatie): number | null => {
    const mins = geselecteerd.filter((g) => g.d.professional_type === d.professional_type).map((g) => g.k.bandbreedte_min).filter((v): v is number => v != null);
    return mins.length ? Math.max(...mins) : null;
  };

  const verstuur = () => {
    if (geselecteerd.length === 0) {
      toast.error('Selecteer minimaal één professional');
      return;
    }
    const ontvangers: Array<{ professional_id: string; professional_type: string; openingsbod?: number | null }> = [];
    const groep: Record<string, number> = {};

    if (modus === 'GROEP') {
      for (const d of disciplinesInSelectie) {
        const bedrag = parseFloat(groepstarieven[d.professional_type]);
        if (!Number.isFinite(bedrag) || bedrag <= 0) {
          toast.error(`Voer een groepstarief in voor ${d.label}`);
          return;
        }
        groep[d.professional_type] = bedrag;
      }
    }
    for (const { d, k } of geselecteerd) {
      let openingsbod: number | null = null;
      if (modus === 'INDIVIDUEEL') {
        const bedrag = parseFloat(biedingen[key(d.professional_type, k.professional_id)]);
        if (!Number.isFinite(bedrag) || bedrag <= 0) {
          toast.error(`Voer een tariefvoorstel in voor ${k.naam}`);
          return;
        }
        openingsbod = bedrag;
      }
      ontvangers.push({ professional_id: k.professional_id, professional_type: d.professional_type, openingsbod });
    }

    verstuurMutation.mutate({
      tariefmodus: modus,
      ontvangers,
      groepstarieven: modus === 'GROEP' ? groep : null,
      bericht: bericht.trim() || null,
      location_id: locationId || null,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !payload) {
    return <div className="p-8 text-center text-red-600">Kon de gradatie-uitlezing niet laden.</div>;
  }

  if (!payload.pool_ingericht) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center">
        <Info className="w-8 h-8 text-blue-600 mx-auto mb-3" />
        <p className="text-gray-700">U heeft nog geen pool ingericht.</p>
        <button
          type="button"
          onClick={() => navigate('/employer/pool-setup')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
        >
          Pool inrichten
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6 pb-32">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Professionals uitnodigen</h1>
        <p className="text-sm text-gray-600 mt-1">
          Nodig in één verzending professionals uit meerdere disciplines uit voor uw medewerkerspool. De uitnodigingstekst is
          voor iedereen gelijk; kies hieronder hoe het uurtarief tot stand komt. De professional reageert met een akkoord,
          een tegenvoorstel of een afwijzing — u hakt de knoop door.
          {payload.werkgebied_radius_km != null && ` Gefilterd op uw werkgebied (${payload.werkgebied_radius_km} km).`}
        </p>
      </div>

      {/* Stap 1: tariefmodus */}
      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">1. Hoe komt het tarief tot stand?</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {MODI.map((m) => {
            const actief = modus === m.key;
            return (
              <button
                key={m.key}
                type="button"
                onClick={() => setModus(m.key)}
                className={`text-left rounded-lg border p-4 transition-colors ${actief ? 'border-[#0B1F3A] bg-blue-50 ring-1 ring-[#0B1F3A]' : 'border-gray-200 hover:bg-gray-50'}`}
              >
                <div className={`flex items-center gap-2 font-medium ${actief ? 'text-[#0B1F3A]' : 'text-gray-900'}`}>
                  {m.icon}
                  {m.titel}
                </div>
                <p className="text-xs text-gray-600 mt-2 leading-relaxed">{m.uitleg}</p>
              </button>
            );
          })}
        </div>
      </section>

      {/* Stap 2: professionals per discipline */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-900">2. Selecteer professionals (alle disciplines uit uw pool)</h2>
        {focusDiscipline && !payload.disciplines.some((d) => d.professional_type === focusDiscipline) && (
          <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
            Deze discipline zit nog niet in uw pool-configuratie. Voeg haar toe via de{' '}
            <Link to="/employer/mijn-belagom?tab=pools" className="underline">Pool-Configurator</Link>; daarna verschijnen hier de kandidaten in uw regio.
          </p>
        )}
        {payload.disciplines.map((d) => {
          const isOpen = open[d.professional_type] ?? (focusDiscipline ? d.professional_type === focusDiscipline : true);
          const dekking = d.actieve_experts >= d.gewenste_experts;
          const beschikbaar = d.kandidaten.filter((k) => !k.al_in_pool && !k.onderhandeling);
          const aantalGekozen = beschikbaar.filter((k) => selectie[key(d.professional_type, k.professional_id)]).length;
          const allesAan = beschikbaar.length > 0 && aantalGekozen === beschikbaar.length;
          const ondergrens = groepOndergrens(d);
          const groepBedrag = parseFloat(groepstarieven[d.professional_type]);
          const groepTeLaag = ondergrens != null && Number.isFinite(groepBedrag) && groepBedrag < ondergrens;

          return (
            <div key={d.professional_type} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
              <div className="w-full flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                <button
                  type="button"
                  onClick={() => setOpen((o) => ({ ...o, [d.professional_type]: !isOpen }))}
                  className="flex items-center gap-2 text-left"
                >
                  <Users className="w-4 h-4 text-blue-600" />
                  <span className="font-semibold text-gray-900">{d.label}</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${dekking ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'}`}>
                    {d.actieve_experts}/{d.gewenste_experts} experts
                  </span>
                  {aantalGekozen > 0 && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">{aantalGekozen} geselecteerd</span>
                  )}
                  {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </button>

                <div className="flex items-center gap-3">
                  {modus === 'GROEP' && aantalGekozen > 0 && (
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-600">Groepstarief €</span>
                      <input
                        type="number"
                        step="0.01"
                        min={0}
                        value={groepstarieven[d.professional_type] ?? ''}
                        onChange={(e) => setGroepstarieven((g) => ({ ...g, [d.professional_type]: e.target.value }))}
                        placeholder={ondergrens != null ? String(ondergrens) : ''}
                        className={`w-24 rounded-md border px-2 py-1 text-sm ${groepTeLaag ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                        title={ondergrens != null ? `Marktconforme ondergrens van de geselecteerde kandidaten: ${euro(ondergrens)}` : undefined}
                      />
                      <span className="text-xs text-gray-500">/uur</span>
                    </div>
                  )}
                  {beschikbaar.length > 0 && (
                    <button
                      type="button"
                      onClick={() => toggleDiscipline(d)}
                      className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-[#0B1F3A]"
                    >
                      {allesAan ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                      {allesAan ? 'Alles uit' : 'Alles selecteren'}
                    </button>
                  )}
                </div>
              </div>
              {modus === 'GROEP' && groepTeLaag && (
                <p className="px-5 pb-2 text-xs text-red-600">
                  Het groepstarief ligt onder de marktconforme ondergrens ({euro(ondergrens)}) van één of meer geselecteerde kandidaten; die uitnodigingen worden geweigerd.
                </p>
              )}

              {isOpen && (
                <div className="border-t border-gray-100 px-5 py-4 space-y-2">
                  {d.kandidaten.length === 0 ? (
                    <p className="text-sm text-gray-400">Geen beschikbare kandidaten binnen uw werkgebied voor deze discipline.</p>
                  ) : (
                    d.kandidaten.map((k) => {
                      const kk = key(d.professional_type, k.professional_id);
                      const isGekozen = !!selectie[kk];
                      const lopend = k.onderhandeling ? STATUS_BADGE[k.onderhandeling.status] : null;
                      const geblokkeerd = k.al_in_pool || !!k.onderhandeling;
                      return (
                        <div key={k.professional_id} className={`rounded-lg border p-3 flex flex-wrap items-center gap-3 ${isGekozen && !geblokkeerd ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}>
                          <input
                            type="checkbox"
                            disabled={geblokkeerd}
                            checked={isGekozen && !geblokkeerd}
                            onChange={() => toggleSelect(d.professional_type, k)}
                            className="w-4 h-4"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">{k.naam}</p>
                            <p className="text-xs text-gray-500 flex flex-wrap items-center gap-x-2">
                              <span>{k.brevet_label}</span>
                              <span>· bandbreedte {euro(k.bandbreedte_min)}{k.bandbreedte_max ? ` – ${euro(k.bandbreedte_max)}` : ''}</span>
                              {k.city && (
                                <span className="inline-flex items-center gap-0.5">
                                  <MapPin className="w-3 h-3" /> {k.city}
                                </span>
                              )}
                              {k.average_rating != null && (
                                <span className="inline-flex items-center gap-0.5">
                                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {k.average_rating.toFixed(1)} ({k.total_reviews})
                                </span>
                              )}
                            </p>
                          </div>
                          {k.al_in_pool ? (
                            <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700">Al in uw pool</span>
                          ) : lopend ? (
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${lopend.cls}`}>{lopend.label}</span>
                          ) : modus === 'INDIVIDUEEL' ? (
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-500">€</span>
                              <input
                                type="number"
                                step="0.01"
                                min={0}
                                disabled={!isGekozen}
                                value={biedingen[kk] ?? ''}
                                onChange={(e) => setBiedingen((b) => ({ ...b, [kk]: e.target.value }))}
                                placeholder={k.bandbreedte_min != null ? String(k.bandbreedte_min) : ''}
                                className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-100"
                              />
                              <span className="text-xs text-gray-500">/uur</span>
                            </div>
                          ) : modus === 'GROEP' ? (
                            <span className="text-xs text-gray-500">
                              {isGekozen && Number.isFinite(groepBedrag) ? `voorstel ${euro(groepBedrag)}/uur` : 'groepstarief'}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-500 italic">professional bepaalt tarief</span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}
      </section>

      {/* Stap 3: bericht + verzenden (sticky) */}
      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
        <h2 className="text-sm font-semibold text-gray-900">3. Uitnodigingstekst (voor iedereen gelijk)</h2>
        <textarea
          value={bericht}
          onChange={(e) => setBericht(e.target.value)}
          placeholder="Beste professional, we nodigen je uit voor onze medewerkerspool… (optioneel)"
          rows={3}
          maxLength={2000}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
        />
        {payload.vestigingen.length > 0 && (
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600">Vestiging (optioneel)</label>
            <select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              className="rounded-md border border-gray-300 px-2 py-1 text-sm"
            >
              <option value="">— geen —</option>
              {payload.vestigingen.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.naam}{v.plaats ? ` (${v.plaats})` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </section>

      <div className="fixed bottom-0 inset-x-0 z-20 border-t border-gray-200 bg-white/95 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-gray-700">
            <span className="font-semibold">{geselecteerd.length}</span> professional{geselecteerd.length === 1 ? '' : 's'} in{' '}
            <span className="font-semibold">{disciplinesInSelectie.length}</span> discipline{disciplinesInSelectie.length === 1 ? '' : 's'}
            <span className="text-gray-400"> · {MODI.find((m) => m.key === modus)?.titel}</span>
          </div>
          <button
            type="button"
            onClick={verstuur}
            disabled={verstuurMutation.isPending || geselecteerd.length === 0}
            className="inline-flex items-center gap-2 px-5 py-2 bg-[#0B1F3A] text-white text-sm font-medium rounded-lg hover:bg-[#1A3459] disabled:opacity-50"
          >
            {verstuurMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Verstuur uitnodiging
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployerPoolUitnodigenPage;
